// ============================================================
// TaskRow.tsx
// Shared row component for displaying a Task in a list. Used by
// TasksView's main list and by ClientSearchView's per-client
// tasks section. Pure presentational — no store reads, no
// network calls. Parent supplies caseLabel / clientLabel
// (resolved via whatever lookup pattern the parent uses) and
// the click + dismiss handlers.
//
// Module-scope helpers (STATUS_LABELS, statusPillClass,
// formatDueDate) are exported alongside the row because they're
// tightly coupled to the rendering. Importable independently if
// any future view wants the status pill or due-date format
// outside a row layout.
// ============================================================
import React from 'react'
import { Task, TaskStatus, TaskPriority } from '../../types/tasks.types'
import FavouriteStar from './FavouriteStar'

// ── STATUS DISPLAY ───────────────────────────────────────────
// Three states, three pill styles. Done uses muted grey + the
// text gets a slightly faded opacity in the row to read as
// "deprioritised but visible".
export const STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  done: 'Done',
}
export const statusPillClass = (status: TaskStatus): string => {
  if (status === 'open') return 'bg-casevo-mint text-casevo-brand'
  if (status === 'in_progress') return 'bg-casevo-light/30 text-casevo-forest'
  return 'bg-gray-100 dark:bg-casevo-border text-casevo-text-soft'
}

// ── PRIORITY DISPLAY ─────────────────────────────────────────
// Severity colours (the dashboard's red/overdue palette), NEVER gold — gold
// is the restrained brand accent. normalisePriority() makes every consumer
// safe for rows that predate the column or arrive without it (a cached row,
// a calendar-built task): anything that isn't 'high'/'low' reads as 'normal'.
export const normalisePriority = (p: unknown): TaskPriority =>
  (p === 'high' || p === 'low' ? p : 'normal')
export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
}
// Row / surface pill. High = red tint (loud); Low = quiet outline (distinct
// from the filled-grey Done pill); Normal isn't shown on rows at all — the
// default stays calm, so only tasks someone deliberately prioritised stand out.
export const priorityPillClass = (p: TaskPriority): string => {
  if (p === 'high') return 'bg-[#fbeae7] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]'
  if (p === 'low') return 'border border-casevo-border text-casevo-text-soft'
  return 'bg-casevo-offwhite text-casevo-mid dark:bg-casevo-border'
}
// Small direction glyph so the pill reads without colour (a11y): ↑ high, ↓ low.
export const PriorityGlyph: React.FC<{ p: TaskPriority; className?: string }> = ({ p, className = 'w-3 h-3' }) => {
  if (p === 'normal') return null
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p === 'high'
        ? <><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></>
        : <><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></>}
    </svg>
  )
}

// ── DUE DATE FORMATTING ──────────────────────────────────────
// Relative for soon (Today / Tomorrow / Overdue by N), absolute for further
// out. SAME rule as My Work (components/tasks/myWorkLogic.ts isTaskOverdue /
// dueYmd — mirrored here, not imported, to avoid a TaskRow↔myWorkLogic import
// cycle): a date-only task (stored at midnight UTC) is due on its stored civil
// day; a timed task is due on its LOCAL day and is overdue once its time has
// passed; a DONE task is never overdue.
const pad2 = (n: number): string => String(n).padStart(2, '0')
const ymdLocalOf = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
const isTimedIso = (iso: string): boolean => { const d = new Date(iso); return d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0 }
const civilDayOf = (iso: string): string => (isTimedIso(iso) ? ymdLocalOf(new Date(iso)) : iso.slice(0, 10))
const dayNumber = (ymd: string): number => Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10))) / 86400000
export const formatDueDate = (iso: string | null, status?: TaskStatus): { text: string; overdue: boolean } | null => {
  if (!iso) return null
  const date = new Date(iso)
  const now = new Date()
  const diffDays = Math.round(dayNumber(civilDayOf(iso)) - dayNumber(ymdLocalOf(now)))
  const absolute = 'Due ' + date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  // Done tasks: plain date, never red.
  if (status === 'done') return { text: diffDays === 0 ? 'Due today' : absolute, overdue: false }
  if (diffDays === 0) {
    // Timed task whose time has already passed today → overdue (matches My Work).
    if (isTimedIso(iso) && date.getTime() < now.getTime()) return { text: 'Overdue (today)', overdue: true }
    return { text: 'Due today', overdue: false }
  }
  if (diffDays === 1) return { text: 'Due tomorrow', overdue: false }
  if (diffDays === -1) return { text: 'Overdue by 1 day', overdue: true }
  if (diffDays < 0) return { text: `Overdue by ${Math.abs(diffDays)} days`, overdue: true }
  if (diffDays < 7) return { text: `Due in ${diffDays} days`, overdue: false }
  return { text: absolute, overdue: false }
}

// ── TASK ROW ─────────────────────────────────────────────────
export interface TaskRowProps {
  task: Task
  caseLabel?: string
  clientLabel?: string
  onClick: () => void
  onDismiss?: () => void   // optional — only present for done tasks the user can dismiss
  // Deep-link deadline/task feature: when true, applies a transient
  // ring around the row so a scroll-to landing catches the eye. The
  // caller (TasksView) drops this back to false via setTimeout after
  // ~2s. Optional — every existing consumer defaults to false so no
  // behaviour changes anywhere else.
  highlighted?: boolean
  // Opt-in favourite star (right side). Off by default so existing
  // consumers (e.g. ClientSearchView's per-client tasks section) are
  // unchanged; TasksView's main list passes it true.
  showFavourite?: boolean
}
const TaskRow: React.FC<TaskRowProps> = ({ task, caseLabel, clientLabel, onClick, onDismiss, highlighted = false, showFavourite = false }) => {
  const due = formatDueDate(task.dueDate, task.status)
  const isDone = task.status === 'done'
  const priority = normalisePriority(task.priority)
  // id="task-row-<id>" is the scroll target the routing helper's
  // TasksView receiver uses to find + scrollIntoView the row.
  // `ring-2 ring-casevo-brand ring-offset-2` when highlighted is
  // the transient glow — falls back to the plain wrapper class
  // otherwise, so every non-highlight render is byte-identical
  // to pre-deep-link behaviour.
  const highlightClass = highlighted
    ? 'ring-2 ring-casevo-brand ring-offset-2 ring-offset-casevo-offwhite'
    : ''
  return (
    <div
      id={`task-row-${task.id}`}
      className={`bg-casevo-surface border border-casevo-border rounded-lg hover:border-casevo-mid focus-within:border-casevo-mid transition-all flex items-stretch ${isDone ? 'opacity-60' : ''} ${highlightClass}`}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex-1 text-left px-4 py-3 flex justify-between items-start gap-3 focus:outline-none casevo-focus min-w-0 hover:bg-casevo-mint/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium text-casevo-forest truncate ${isDone ? 'line-through' : ''}`}>{task.title}</p>
          {(caseLabel || clientLabel) && (
            <p className="text-xs text-casevo-mid truncate mt-0.5">
              {caseLabel ? `Case: ${caseLabel}` : `Client: ${clientLabel}`}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="flex items-center gap-1.5">
            {priority !== 'normal' && (
              <span
                className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${priorityPillClass(priority)}`}
                title={`${PRIORITY_LABELS[priority]} priority`}
              >
                <PriorityGlyph p={priority} />{PRIORITY_LABELS[priority]}
              </span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full ${statusPillClass(task.status)}`}>{STATUS_LABELS[task.status]}</span>
          </div>
          {due && (
            <span className={`text-xs ${due.overdue ? 'text-casevo-danger' : 'text-casevo-mid'}`}>{due.text}</span>
          )}
        </div>
      </button>
      {showFavourite && (
        // Sibling of the open-button (not nested) so its click doesn't
        // trigger onClick; the star's own stopPropagation covers bubbling.
        // self-center so it doesn't stretch to the items-stretch row height.
        <FavouriteStar
          itemType="task"
          itemId={task.id}
          label={task.title}
          variant="light"
          className="self-center mr-1"
        />
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          title="Remove task"
          aria-label={`Remove ${task.title}`}
          className="px-3 flex items-center justify-center text-casevo-mid hover:text-casevo-danger hover:bg-casevo-mint/30 border-l border-casevo-border focus:outline-none focus-visible:text-casevo-danger transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  )
}
export default TaskRow
